import React, { useContext, useEffect, useState } from 'react'
import OrderOptions from 'app/React/Components/Main/OrderOptions'
import StepImage from 'app/React/Components/Main/StepImage'
import ColorImage from 'app/React/Components/Main/ColorImage'
import Price from 'app/React/Components/Footer/Price'
import DepositField from 'app/React/Components/Footer/DepositField'
import EstimatedDelivery from 'app/React/Components/Form/EstimatedDelivery'
import IsLeasing from 'app/React/Components/misc/IsLeasing'
import Tip from 'app/React/Components/misc/Tip'
import clx from 'classnames'
import { useTranslation } from 'utils/useTranslation'
import GuaranteePriceField from "app/React/Components/Footer/GuaranteePriceField";
import SecurityDepositLeasingOrderOptions from "app/React/Components/Main/SecurityDepositLeasingOrderOptions";
import { ConfiguratorContext } from "app/React/ConfiguratorContainer";
import SecurityDeposit from "app/React/Components/Footer/SecurityDeposit";

export default function CheckoutOverview() {
  const {
    car,
    colorSettingId,
    currentVariant,
    isAccessoryShop,
    isDealerConfigurator,
    isGlobalDownPaymentLeasing,
    hideSecurityDeposit,
    lang,
    order,
    strings,
    useSecurityDeposit
  } = useContext(ConfiguratorContext)

  const accessoryStrings = useTranslation('accessories_shop.checkout') ?? {}
  const securityDeposit = useSecurityDeposit()

  const leasingPeriod = currentVariant.leasing_period ?? 36

  const [totalPrice, setTotalPrice] = useState(0)
  const [totalLeasingPeriodPrice, setTotalLeasingPeriodPrice] = useState(0)

  const showPrice = Boolean(Number(currentVariant.show_price ?? null))
  const showTotalPrice = !Number(currentVariant.hide_total_price ?? null)
  const showTotalLeasingPeriodPrice = showTotalPrice && strings?.price?.total_total_period !== undefined && strings?.price?.total_leasing_period !== undefined

  let color = null

  if (!isAccessoryShop) {
    Object.keys(order.configuration).forEach((config) => {
      if (order.configuration[config] === null) return
      if (order.configuration[config].setting_id === colorSettingId) {
        color = order.configuration[config].title
      }
    })
  }

  useEffect(() => {
    setTotalPrice(order.price * parseInt(leasingPeriod) + parseInt(securityDeposit) + parseInt(car.delivery_fee))
    setTotalLeasingPeriodPrice(order.price * 12 + parseInt(securityDeposit) + parseInt(car.delivery_fee))
  }, [order, leasingPeriod, securityDeposit])

  const totalText = () => {
    const tooltipText = strings.price[`total_${order.paymentType}_tip`] ?? ''
    if (strings.price[`total_show_${order.paymentType}_tip`] !== true || tooltipText.length === 0) {
      return order.paymentType === 'cash' ? strings.price.total : strings.price.total_monthly_price
    }

    return (
      <div className='has-tooltip'>
        {strings.price.total}
        <Tip content={tooltipText}/>
      </div>
    )
  }

  return (
    <div className='config-overview'>
      {isAccessoryShop ? null : (
        <div className='config-overview__image-container'>
          <ColorImage>
            <StepImage step={1}/>
          </ColorImage>
        </div>
      )}
      {isAccessoryShop ? (
        <div className='config-overview__car-details'>
          <h2 className='config-overview__car-title'>{accessoryStrings.title}</h2>
          <p className='config-overview__car-color'>{accessoryStrings.click_and_collect_title}</p>
          <div
            className={clx('mandatory-information')}
            dangerouslySetInnerHTML={{ __html: accessoryStrings.click_and_collect_text }}
          />
        </div>
      ) : (
        <div className='config-overview__car-details'>
          <h2 className='config-overview__car-title'>{car.title}</h2>
          <p className='config-overview__car-color'>{color}</p>

          {
            !isDealerConfigurator ? (
              <div
                className='overview'
                dangerouslySetInnerHTML={{ __html: currentVariant.mandatory_information ?? '' }}
              />
            ) : null
          }
          <EstimatedDelivery/>
        </div>
      )}

      <div className='config-overview__overview'>
        <div className='config-overview__overview-title'>
          <div>
            {strings.misc.overview}
            <div className='config-tooltip'/>
          </div>
        </div>
        <div className='config-overview__prices'>
          <OrderOptions/>
        </div>
        {order.paymentType === 'leasing' && typeof order.leasing_price_range === 'object' && (
          <div className='config-overview__prices'>
            <div className='config-overview__price'>
              <div>{strings.payment_form.leasing_range.label}</div>
              <div>
                {new Intl.NumberFormat(lang).format(order.leasing_price_range.from_km)}
                {' '}
                {strings.payment_form.leasing_range.km}
              </div>
            </div>
          </div>
        )}
        <SecurityDepositLeasingOrderOptions/>
        {
          isAccessoryShop || hideSecurityDeposit
            ? null
            : order.paymentType === 'cash'
              ? <DepositField layout='overview' useDefaultDepositLabel/>
              : order.paymentType === 'leasing' && isGlobalDownPaymentLeasing
                ? <DepositField layout='overview' />
                : <DepositField layout='overview' useDefaultDepositLabel/>
        }
        {isAccessoryShop ? null : <GuaranteePriceField/>}
        {showPrice ? (
          <div className='config-overview__title'>
            <div>
              <IsLeasing>
                {isGlobalDownPaymentLeasing && (<div>{strings.price.total_deposit}</div>)}
              </IsLeasing>
              {totalText()}
              <IsLeasing>
                {showTotalPrice ? <span>{strings.price.total_total}</span> : null}
                {showTotalLeasingPeriodPrice ? <span>{strings.price.total_total_period}</span> : null}
              </IsLeasing>
            </div>
            <div className='fit-content'>
              <IsLeasing>
                {isGlobalDownPaymentLeasing && (
                  <SecurityDeposit type='security-deposit'/>
                )}
              </IsLeasing>
              <Price classname='price total'/>
              <IsLeasing>
                {showTotalPrice ? (
                  <span>
                    <Price price={totalPrice} format='cash'/>
                    {strings.price.total_36}
                  </span>
                ) : null}
                {showTotalLeasingPeriodPrice ? (
                  <span>
                    <Price price={totalLeasingPeriodPrice} format='cash'/>
                    {strings.price.total_leasing_period}
                  </span>
                ) : null}
              </IsLeasing>
              <span>{strings.price.incl_vat}</span>
              <IsLeasing>
                <span>{strings.price.and_delivery_fee}</span>
              </IsLeasing>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
